import type {
    IncomeSplitConfig,
    IncomeSplitHistory,
} from '@fruitful/income-split';
import type {
    MembershipIncome,
    MembershipProfileAnswerWithQuestion,
    ProfileAnswer,
} from '@fruitful/member-profile';
import type { MembershipSpend } from '@fruitful/member-profile/src/spend/types/Spend';
import type {
    Goal,
    GoalGroup,
    GoalTask,
    GuidanceSession,
    HolisticSummary,
    StripeSubscriptionSummary,
    SubscriptionProduct,
} from '@fruitful/types';
import { useQueryClient } from '@tanstack/react-query';
import { type PrimitiveAtom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useState } from 'react';
import { useToastController } from 'smoothie';

import type {
    CommonMutationOptions,
    ContainerComponent,
} from '../../common/types/Common';
import { requestBuilder } from '../../http/RequestBuilder';
import {
    createdGoalAtom,
    createdGoalTaskAtom,
    isEditingGoalsAtom,
    mutatedGoalAtom,
    mutatedGoalTaskAtom,
    mutatedMembershipAtom,
    newGoalToExpandAtom,
    selectedMembershipAtom,
    selectedMembershipIdAtom,
    updatedGoalsAtom,
    updatedGoalTasksAtom,
} from '../entityAtoms';
import type {
    Bill,
    DirectDepositSwitch,
    GoalCategory,
    GuideStatus,
    Member,
    MembershipDetails,
    Paycheck,
    PaycheckRoutingConfig,
    SendbirdGroupChannel,
    TransactionStats,
} from '../entityTypes';
import {
    decodeNewGoalGroupTempId,
    newGoalGroupNeeded,
} from './components/goals/Goals';
import { transposeMembershipDetailsDto } from './dto/MembershipDto';
import { useMembershipBills } from './hooks/useMembershipBills';
import { useMembershipHolisticSummary } from './hooks/useMembershipHolisticSummary';
import type { MembershipGuidanceNotes } from './hooks/useUpsertMembershipGuidanceNotes';
import { AllMembershipsQueryKey } from './MembershipEntityProvider';

export interface MembershipGuide {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    profileImageUrl: string | null;
    status: GuideStatus;
    sendbirdUserId: string | null;
}

export interface MembershipDetailsDto {
    id: string;
    guideId: string | null;
    cancellationScheduledJobArn: string | null;
    createdAt: string;
    updatedAt: string | null;
    membershipYearEndsAt?: string | null;
    stripeProductName?: string | null;
    stripeBillingFrequency?: string | null;
    hasPlusFeatures?: boolean;
    product?: SubscriptionProduct | null;
    users: Member[];
    guide: MembershipGuide | null;
    guidanceSession: GuidanceSession[];
    goalGroups: GoalGroup[];
    transactionStatsByMonth: TransactionStats[];
    allGoalCategories: GoalCategory[];
    messagingChannel: SendbirdGroupChannel | null;
    subscription: StripeSubscriptionSummary | null;
    paycheckRoutingConfigs: PaycheckRoutingConfig[];
    incomeSplitConfig: IncomeSplitConfig | null;
    incomeSplitHistory?: IncomeSplitHistory[];
    paychecks: Paycheck[];
    membershipIncomes: MembershipIncome[];
    membershipProfileAnswers: Array<
        MembershipProfileAnswerWithQuestion<ProfileAnswer>
    >;
    directDepositSwitches: DirectDepositSwitch[];
    membershipSpend: MembershipSpend | null;
    membershipGuidanceNotes: MembershipGuidanceNotes | null;
    // Populated async
    holisticSummary: HolisticSummary | undefined;
    bills: Bill[] | undefined;
}

export const getMembershipDetailsQueryKey = ({
    membershipId,
}: {
    membershipId?: string | null;
}): string => `getMembershipDetails/${membershipId ?? ''}`;

export const useGetMembershipDetails = requestBuilder.query<
    MembershipDetails,
    { membershipId: string | null }
>({
    endpoint: ({ membershipId }) => `/api/admin/memberships/${membershipId}`,
    queryKey: getMembershipDetailsQueryKey,
});

export const MembershipDetailsEntityProvider: ContainerComponent = ({
    children,
}) => {
    const membershipId = useAtomValue(selectedMembershipIdAtom);
    const setMembershipDetails = useSetAtom(selectedMembershipAtom);
    const setMembershipId = useSetAtom(selectedMembershipIdAtom);
    const isEditingGoals = useAtomValue(isEditingGoalsAtom);
    const toast = useToastController();

    const { data: membershipDetails } = useGetMembershipDetails(
        { membershipId },
        {
            enabled: membershipId !== null, // TODO we could restrict this call based on adminUser.authorization permissions -- need to handle UI in this case
            select: useCallback(
                (data: MembershipDetailsDto) =>
                    transposeMembershipDetailsDto(data),
                [],
            ),
            // Conditionally setting these to false to avoid a re-render wiping away any in-progress changes
            refetchOnReconnect: !isEditingGoals,
            refetchOnWindowFocus: !isEditingGoals,
        },
    );
    const { membershipHolisticSummary } =
        useMembershipHolisticSummary(membershipId);
    const { membershipBills } = useMembershipBills(membershipId);

    useEffect(() => {
        if (membershipDetails?.id === membershipId) {
            const allDetails = {
                ...membershipDetails,
                holisticSummary:
                    membershipHolisticSummary.membershipId === membershipId
                        ? membershipHolisticSummary.holisticSummary
                        : undefined,
                bills:
                    membershipBills.membershipId === membershipId
                        ? membershipBills.bills?.map((b) => {
                              // filter out bill transactions because some members have enough
                              // to break local storage, but they aren't used anywhere in grove
                              return {
                                  ...b,
                                  transactions: [],
                              };
                          })
                        : undefined,
            };

            try {
                setMembershipDetails(allDetails);
            } catch (error) {
                setMembershipDetails(null);
                setMembershipId(null);
                toast.show('Could not load this member', { duration: 2000 });
            }
        }
    }, [
        membershipId,
        membershipDetails,
        membershipHolisticSummary,
        membershipBills,
    ]);

    useMembershipMutations(membershipId);
    useGoalsMutations(membershipId);

    return <>{children}</>;
};

const useUpdateMembership = requestBuilder.mutation<
    MembershipDetailsDto,
    Pick<MembershipDetails, 'guideId'>,
    Pick<MembershipDetails, 'id'>
>({
    endpoint: ({ id }) => `/api/admin/memberships/${id}`,
    method: 'PATCH',
});

export const getMembershipUpdateMutationOptions = (
    membershipId: string | null,
): CommonMutationOptions => {
    const queryClient = useQueryClient();
    const membershipDetailsQueryKey = getMembershipDetailsQueryKey({
        membershipId,
    });

    return {
        onMutate: () => {
            // Todo update local state?
            // queryClient.setQueryData(['todo', { id: variables.id }], optimisticUpdate);
            // and maybe atom: setMembershipDetails(...)
            void queryClient.cancelQueries({
                queryKey: [membershipDetailsQueryKey],
                exact: true,
            });

            void queryClient.cancelQueries({
                queryKey: [AllMembershipsQueryKey],
                exact: true,
            });
        },
        onSuccess: () => {
            // Todo consider updating local state with result rather than invalidating/refetching the query
        },
        onSettled: () => {
            void queryClient.invalidateQueries({
                queryKey: [membershipDetailsQueryKey],
                exact: true,
            });

            void queryClient.invalidateQueries({
                queryKey: [AllMembershipsQueryKey],
                exact: true,
            });
        },
    };
};

const useMembershipMutations = (membershipId: string | null) => {
    const commonMutationOptions =
        getMembershipUpdateMutationOptions(membershipId);
    useHandleMembershipUpdate(commonMutationOptions);
};

const useHandleMembershipUpdate = (mutationOptions: CommonMutationOptions) => {
    const [mutatedMembership, setMutatedMembership] = useAtom(
        mutatedMembershipAtom,
    );
    const { mutate: mutateMembership } = useUpdateMembership(mutationOptions);

    useEffect(() => {
        if (mutatedMembership !== null) {
            const { guideId } = mutatedMembership;

            mutateMembership({
                urlParams: { id: mutatedMembership.id },
                body: {
                    guideId,
                },
            });

            setMutatedMembership(null);
        }
    }, [mutatedMembership]);
};

export const useCreateGoalGroup = requestBuilder.mutation<
    GoalGroup,
    Pick<GoalGroup, 'membershipId' | 'goalCategoryId'>
>({
    endpoint: '/api/admin/goal-groups/',
    method: 'POST',
});

const useMutateGoal = requestBuilder.mutation<
    Goal,
    Pick<
        Goal,
        | 'goalGroupId'
        | 'status'
        | 'position'
        | 'description'
        | 'targetAmount'
        | 'targetDate'
    >,
    Pick<Goal, 'id'>
>({
    endpoint: ({ id }) => `/api/admin/goals/${id}`,
    method: 'PATCH',
});

export const useCreateGoal = requestBuilder.mutation<
    Goal,
    Pick<
        Goal,
        'description' | 'position' | 'status' | 'targetAmount' | 'targetDate'
    >,
    Pick<Goal, 'goalGroupId'>
>({
    endpoint: ({ goalGroupId }) =>
        `/api/admin/goal-groups/${goalGroupId}/goals/`,
    method: 'POST',
});

type UpdateGoalItems = Array<Pick<Goal, 'id' | 'position'>>;
const useUpdateGoals = requestBuilder.mutation<
    { goals: UpdateGoalItems },
    { goals: UpdateGoalItems }
>({
    endpoint: '/api/admin/goals',
    method: 'PATCH',
});

const useMutateGoalTask = requestBuilder.mutation<
    GoalTask,
    Pick<GoalTask, 'status' | 'position' | 'description'>,
    Pick<GoalTask, 'id'>
>({
    endpoint: ({ id }) => `/api/admin/goal-tasks/${id}`,
    method: 'PATCH',
});

export const useCreateGoalTask = requestBuilder.mutation<
    GoalTask,
    Pick<GoalTask, 'position' | 'description'>,
    Pick<GoalTask, 'goalId'>
>({
    endpoint: ({ goalId }) => `/api/admin/goals/${goalId}/goal-tasks/`,
    method: 'POST',
});

type UpdateGoalTaskItems = Array<Pick<GoalTask, 'id' | 'position'>>;
const useUpdateGoalTasks = requestBuilder.mutation<
    { goalTasks: UpdateGoalTaskItems },
    { goalTasks: UpdateGoalTaskItems }
>({
    endpoint: '/api/admin/goal-tasks',
    method: 'PATCH',
});

const useGoalsMutations = (membershipId: string | null) => {
    const queryClient = useQueryClient();
    const membershipDetailsQueryKey = getMembershipDetailsQueryKey({
        membershipId,
    });
    const commonMutationOptions: CommonMutationOptions = {
        onMutate: () => {
            // Todo update local state?
            // queryClient.setQueryData(['todo', { id: variables.id }], optimisticUpdate);
            // and maybe atom: setMembershipDetails(...)
            void queryClient.cancelQueries({
                queryKey: [membershipDetailsQueryKey],
                exact: true,
            });
        },
        onSuccess: () => {
            // Todo consider updating local state with result rather than invalidating/refetching the query
        },
        onSettled: () => {
            void queryClient.invalidateQueries({
                queryKey: [membershipDetailsQueryKey],
                exact: true,
            });
        },
    };

    useHandleGoalUpdate(commonMutationOptions);
    useHandleGoalCreation(commonMutationOptions);
    useHandleBatchGoalsUpdate(commonMutationOptions);
    useHandleGoalTaskUpdate(commonMutationOptions);
    useHandleGoalTaskCreation(commonMutationOptions);
    useHandleBatchGoalTaskUpdate(commonMutationOptions);
};

const useCreatePrerequisiteGoalGroup = (
    goalAtom: PrimitiveAtom<Goal | null>,
) => {
    const membershipId = useAtomValue(selectedMembershipIdAtom);
    const goalAtomSetter = useSetAtom(goalAtom);
    const [goalAwaitingGroup, setGoalAwaitingGroup] = useState<Goal | null>(
        null,
    );

    const { mutate: createGoalGroup } = useCreateGoalGroup({
        onSuccess: useCallback(
            (goalGroup: GoalGroup) => {
                if (goalAwaitingGroup !== null) {
                    // Set the goal that was awaiting a group to the provided atom, so the create/update mutation can now proceed
                    goalAtomSetter({
                        ...goalAwaitingGroup,
                        goalGroupId: goalGroup.id,
                    });
                    setGoalAwaitingGroup(null);
                }
            },
            [goalAwaitingGroup],
        ),
    });

    const createPrerequisiteGoalGroup = (
        goalGroupId: string | undefined,
        goalValue: Goal,
    ) => {
        setGoalAwaitingGroup({ ...goalValue }); // Set local state so when this mutation is done, we can continue with creating this goal
        const goalCategoryId =
            goalGroupId !== undefined
                ? decodeNewGoalGroupTempId(goalGroupId)?.goalCategoryId
                : undefined;
        if (membershipId !== null && goalCategoryId !== undefined) {
            createGoalGroup({
                body: {
                    membershipId,
                    goalCategoryId,
                },
            });
        }
    };

    return { createPrerequisiteGoalGroup };
};

const useHandleGoalUpdate = (mutationOptions: CommonMutationOptions) => {
    const [mutatedGoal, setMutatedGoal] = useAtom(mutatedGoalAtom);
    const { createPrerequisiteGoalGroup } =
        useCreatePrerequisiteGoalGroup(mutatedGoalAtom);
    const { mutate: mutateGoal } = useMutateGoal(mutationOptions);
    useEffect(() => {
        if (mutatedGoal !== null) {
            const {
                goalGroupId,
                status,
                position,
                description,
                targetAmount,
                targetDate,
            } = mutatedGoal;

            if (newGoalGroupNeeded(goalGroupId)) {
                createPrerequisiteGoalGroup(goalGroupId, mutatedGoal);
            } else {
                mutateGoal({
                    urlParams: { id: mutatedGoal.id },
                    body: {
                        goalGroupId,
                        status,
                        position,
                        description,
                        targetAmount,
                        targetDate,
                    },
                });
                setMutatedGoal(null);
            }
        }
    }, [mutatedGoal]);
};

const useHandleGoalCreation = (mutationOptions: CommonMutationOptions) => {
    const [createdGoal, setCreatedGoal] = useAtom(createdGoalAtom);
    const setNewGoalToExpand = useSetAtom(newGoalToExpandAtom);
    const { createPrerequisiteGoalGroup } =
        useCreatePrerequisiteGoalGroup(createdGoalAtom);

    const { mutate: createGoal } = useCreateGoal({
        ...mutationOptions,
        onSuccess: (goal: Goal) => {
            setNewGoalToExpand(goal.id);
            mutationOptions.onSuccess();
        },
    });
    useEffect(() => {
        if (createdGoal !== null) {
            const {
                goalGroupId,
                description,
                position,
                status,
                targetAmount,
                targetDate,
            } = createdGoal;
            if (newGoalGroupNeeded(goalGroupId)) {
                createPrerequisiteGoalGroup(goalGroupId, createdGoal);
            } else {
                createGoal({
                    urlParams: { goalGroupId },
                    body: {
                        description,
                        position,
                        status,
                        targetAmount,
                        targetDate,
                    },
                });
            }
            setCreatedGoal(null);
        }
    }, [createdGoal]);
};

const useHandleBatchGoalsUpdate = (mutationOptions: CommonMutationOptions) => {
    const [updatedGoals, setUpdatedGoals] = useAtom(updatedGoalsAtom);
    const { mutate: reorderGoals } = useUpdateGoals(mutationOptions);
    useEffect(() => {
        if (updatedGoals !== null) {
            reorderGoals({
                body: {
                    goals: updatedGoals.map((g) => ({
                        id: g.id,
                        position: g.position,
                    })),
                },
            });
            setUpdatedGoals(null);
        }
    }, [updatedGoals]);
};

const useHandleGoalTaskUpdate = (mutationOptions: CommonMutationOptions) => {
    const [mutatedGoalTask, setMutatedGoalTask] = useAtom(mutatedGoalTaskAtom);
    const { mutate: mutateGoalTask } = useMutateGoalTask(mutationOptions);
    useEffect(() => {
        if (mutatedGoalTask !== null) {
            const { id, status, position, description } = mutatedGoalTask;
            mutateGoalTask({
                urlParams: { id },
                body: { status, position, description },
            });
            setMutatedGoalTask(null);
        }
    }, [mutatedGoalTask]);
};

const useHandleGoalTaskCreation = (mutationOptions: CommonMutationOptions) => {
    const [createdGoalTask, setCreatedGoalTask] = useAtom(createdGoalTaskAtom);
    const { mutate: createGoalTask } = useCreateGoalTask(mutationOptions);
    useEffect(() => {
        if (createdGoalTask !== null) {
            const { goalId, position, description } = createdGoalTask;
            createGoalTask({
                urlParams: { goalId },
                body: { position, description },
            });
            setCreatedGoalTask(null);
        }
    }, [createdGoalTask]);
};

const useHandleBatchGoalTaskUpdate = (
    mutationOptions: CommonMutationOptions,
) => {
    const [updatedGoalTasks, setUpdatedGoalTasks] =
        useAtom(updatedGoalTasksAtom);
    const { mutate: updateGoalTasks } = useUpdateGoalTasks(mutationOptions);
    useEffect(() => {
        if (updatedGoalTasks !== null) {
            updateGoalTasks({
                body: {
                    goalTasks: updatedGoalTasks.map((t) => ({
                        id: t.id,
                        position: t.position,
                    })),
                },
            });
            setUpdatedGoalTasks(null);
        }
    }, [updatedGoalTasks]);
};
